import { useEffect, useMemo, useState } from 'react';
import { useRows } from '@/hooks/useLive';
import { save, softDelete, upsert } from '@/lib/crud';
import { dateTime, hours as fmtHours, humanise, num, isoDate } from '@/lib/format';
import { Drawer } from '@/components/ui/Drawer';
import { Evidence } from '@/components/evidence/Evidence';
import { Button } from '@/components/ui/Button';
import { ConfirmRemove } from '@/components/ui/ConfirmRemove';
import { StatusChip } from '@/components/ui/StatusChip';
import {
  DateTimeInput, NumberInput, SelectInput, TextArea, TextInput,
} from '@/components/ui/Field';
import { meterSuffix, unitOf } from '@/lib/meter';
import type { Machine, Site } from '@/types/models';
import { atQuarry } from '@/lib/fleet';
import { BreakdownJobCard } from '../workshop/JobCardLink';

interface Breakdown extends Record<string, unknown> {
  id: string;
  breakdown_no: string;
  machine_id: string;
  category_id: string | null;
  severity: string;
  started_at: string;
  ended_at: string | null;
  downtime_hours: number | null;
  breakdown_date: string;
  hour_meter_at_failure: number | null;
  failure_description: string;
  root_cause: string | null;
  action_taken: string | null;
  technician: string | null;
  status: string;
  returned_to_service_at: string | null;
  production_impact_t: number | null;
  comments: string | null;
}

interface BreakdownPart {
  id: string;
  breakdown_id: string;
  part_id: string | null;
  free_text_part: string | null;
  qty_required: number;
  qty_issued: number;
  is_blocking: boolean;
  eta_date: string | null;
  notes: string | null;
}

/** The workflow a repair actually follows. Status drives machine
 *  availability, so it is a first-class field rather than a note. */
const STATUSES = [
  { value: 'reported', label: 'Reported — not yet looked at' },
  { value: 'diagnosed', label: 'Diagnosed' },
  { value: 'awaiting_parts', label: 'Awaiting parts' },
  { value: 'in_progress', label: 'Repair in progress' },
  { value: 'repaired', label: 'Repaired — back in service' },
  { value: 'closed', label: 'Closed' },
  { value: 'cancelled', label: 'Cancelled' },
];

const SEVERITIES = [
  { value: 'minor', label: 'Minor — machine still working' },
  { value: 'major', label: 'Major — machine stopped' },
  { value: 'critical', label: 'Critical — production affected' },
];


export function BreakdownDrawer({
  breakdown, onClose, locked,
}: { breakdown: Partial<Breakdown>; onClose: () => void; locked?: string | null }) {
  const isNew = !breakdown.id;
  const machines = useRows<Machine>('machines', 'fleet_no');
  const sites = useRows<Site>('sites');
  const categories = useRows<Record<string, unknown>>('breakdown_categories', 'sort_order');
  const parts = useRows<Record<string, unknown>>('parts', 'part_no');
  const allLines = useRows<BreakdownPart>('breakdown_parts', 'id');

  const [v, setV] = useState<Record<string, unknown>>({});
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [busy, setBusy] = useState(false);

  const [partId, setPartId] = useState<string | null>(null);
  const [freeText, setFreeText] = useState('');
  const [qty, setQty] = useState<number | null>(1);
  const [eta, setEta] = useState('');

  useEffect(() => {
    setV({
      severity: 'major',
      status: 'reported',
      started_at: new Date().toISOString(),
      ...breakdown,
    });
  }, [breakdown]);

  const set = (k: string, val: unknown) => setV((d) => ({ ...d, [k]: val }));

  const lines = useMemo(
    () => (allLines ?? []).filter((l) => l.breakdown_id === breakdown.id),
    [allLines, breakdown.id],
  );

  const machine = machines?.find((m) => m.id === v.machine_id);
  const isOpen = ['reported', 'diagnosed', 'awaiting_parts', 'in_progress'].includes(String(v.status));

  // Live downtime for a breakdown that is still open.
  const runningHours = v.started_at && !v.ended_at
    ? (Date.now() - new Date(String(v.started_at)).getTime()) / 3_600_000
    : null;

  function validate(): boolean {
    const e: Record<string, string> = {};
    if (!v.machine_id) e.machine_id = 'Choose a machine';
    if (!v.started_at) e.started_at = 'When did it stop?';
    if (!v.failure_description) e.failure_description = 'Describe the failure';
    if (v.ended_at && v.started_at && String(v.ended_at) < String(v.started_at)) {
      e.ended_at = 'Repair cannot end before the breakdown started';
    }
    if (['repaired', 'closed'].includes(String(v.status)) && !v.ended_at) {
      e.ended_at = 'A repaired breakdown needs an end time — it is what stops the downtime clock';
    }
    setErrors(e);
    return Object.keys(e).length === 0;
  }

  async function submit() {
    if (!validate()) return;
    setBusy(true);
    const payload = { ...v };
    if (breakdown.id) payload.id = breakdown.id;
    if (!payload.breakdown_date && payload.started_at) {
      // The site's calendar day the machine stopped on, not UTC's.
      payload.breakdown_date = isoDate(new Date(String(payload.started_at)));
    }
    const ok = await save(
      () => upsert('breakdowns', payload as Record<string, unknown> & { id?: string }),
      'Breakdown saved',
    );
    setBusy(false);
    if (ok) onClose();
  }

  async function addPart() {
    if (!partId && !freeText.trim()) return;
    setBusy(true);
    const ok = await save(
      () => upsert('breakdown_parts', {
        breakdown_id: breakdown.id,
        part_id: partId,
        free_text_part: partId ? null : freeText.trim(),
        qty_required: qty ?? 1,
        is_blocking: true,
        eta_date: eta || null,
      }),
      'Part added',
    );
    setBusy(false);
    if (ok) { setPartId(null); setFreeText(''); setQty(1); setEta(''); }
  }

  return (
    <Drawer
      open
      locked={locked}
      width={640}
      title={isNew ? 'Log a breakdown' : String(breakdown.breakdown_no)}
      subtitle={machine ? `${machine.fleet_no} — ${machine.manufacturer ?? ''} ${machine.model ?? ''}`.trim() : undefined}
      onClose={onClose}
      footer={
        <>
          {!isNew && (
            <ConfirmRemove
              disabled={busy}
              label="Remove"
              question="Remove this breakdown?"
              onConfirm={async () => {
                const ok = await save(() => softDelete('breakdowns', breakdown.id as string), 'Breakdown removed');
                // Close only if it went through. A refusal leaves the record
                // open with the reason on screen, rather than closing over it.
                if (ok !== null) onClose();
                return ok !== null;
              }}
            />
          )}
          <span className="spacer" />
          <Button variant="secondary" onClick={onClose} disabled={busy}>Cancel</Button>
          <Button onClick={submit} loading={busy}>Save</Button>
        </>
      }
    >
      {!isNew && (
        <div className="row" style={{ marginBottom: 'var(--sp-3)', flexWrap: 'wrap' }}>
          <StatusChip tone={isOpen ? 'red' : 'green'}>
            {isOpen ? 'Machine is down' : 'Back in service'}
          </StatusChip>
          <span className="small muted">
            {v.ended_at
              ? `${fmtHours(breakdown.downtime_hours)} h total downtime`
              : runningHours != null
                ? `${fmtHours(runningHours)} h and counting`
                : ''}
          </span>
        </div>
      )}
      {!isNew && (
        <BreakdownJobCard breakdown={{
          id: String(breakdown.id), machine_id: String(breakdown.machine_id),
          failure_description: (breakdown.failure_description as string | null) ?? null,
          severity: (breakdown.severity as string | null) ?? null,
          hour_meter_at_failure: (breakdown.hour_meter_at_failure as number | null) ?? null,
          status: (breakdown.status as string | null) ?? null,
        }} />
      )}

      <div className="form-grid">
        <div className="form-section field--full">Failure</div>
        <SelectInput id="bd-machine" label="Machine" required error={errors.machine_id}
          value={(v.machine_id as string) ?? null}
          options={(machines ?? []).filter((m) => m.is_active || m.id === v.machine_id).map((m) => ({
            value: m.id,
            label: atQuarry(m) ? m.fleet_no
              : `${m.fleet_no} · ${(sites ?? []).find((x) => x.id === m.site_id)?.name ?? 'branch'}`,
          }))}
          onChange={(x) => {
            set('machine_id', x);
            const m = machines?.find((y) => y.id === x);
            if (m && v.hour_meter_at_failure == null) set('hour_meter_at_failure', m.current_hour_meter);
          }} />
        <SelectInput id="bd-cat" label="Category" value={(v.category_id as string) ?? null}
          options={(categories ?? []).map((c) => ({ value: String(c.id), label: String(c.name) }))}
          onChange={(x) => set('category_id', x)}
          help="Drives the recurring-failure analysis in the monthly report." />

        <SelectInput id="bd-sev" label="Severity" value={(v.severity as string) ?? 'major'}
          options={SEVERITIES} onChange={(x) => set('severity', x)} />
        <NumberInput id="bd-meter"
          label={`${unitOf(machines?.find((y) => y.id === v.machine_id)) === 'km' ? 'Odometer' : 'Hour meter'} at failure`}
          suffix={meterSuffix(unitOf(machines?.find((y) => y.id === v.machine_id)))} min={0} step={0.1}
          value={(v.hour_meter_at_failure as number) ?? null}
          onChange={(x) => set('hour_meter_at_failure', x)} />

        <div className="field--full">
          <TextArea id="bd-desc" label="What failed" required error={errors.failure_description}
            value={String(v.failure_description ?? '')}
            onChange={(x) => set('failure_description', x)} />
        </div>

        <div className="form-section field--full">Downtime</div>
        <DateTimeInput id="bd-start" label="Stopped at" required error={errors.started_at}
          value={(v.started_at as string) ?? null}
          onChange={(x) => set('started_at', x)} />
        <DateTimeInput id="bd-end" label="Back in service at" error={errors.ended_at}
          value={(v.ended_at as string) ?? null}
          onChange={(x) => set('ended_at', x)}
          help="Leave blank while the machine is still down." />

        <SelectInput id="bd-status" label="Repair status" value={(v.status as string) ?? 'reported'}
          options={STATUSES} onChange={(x) => set('status', x)}
          help="Anything other than repaired, closed or cancelled keeps the machine marked down." />
        <NumberInput id="bd-impact" label="Production lost" suffix="t" min={0} step={10}
          value={(v.production_impact_t as number) ?? null}
          onChange={(x) => set('production_impact_t', x)} />

        <div className="form-section field--full">Diagnosis</div>
        <TextInput id="bd-tech" label="Technician" value={String(v.technician ?? '')}
          onChange={(x) => set('technician', x || null)} />
        <DateTimeInput id="bd-rts" label="Returned to service"
          value={(v.returned_to_service_at as string) ?? null}
          onChange={(x) => set('returned_to_service_at', x)} />

        <div className="field--full">
          <TextArea id="bd-cause" label="Root cause" value={String(v.root_cause ?? '')}
            onChange={(x) => set('root_cause', x || null)}
            help="What actually caused it — this is what stops the same failure recurring." />
        </div>
        <div className="field--full">
          <TextArea id="bd-action" label="Action taken" value={String(v.action_taken ?? '')}
            onChange={(x) => set('action_taken', x || null)} />
        </div>
      </div>

      {!isNew && (
        <>
          <div className="form-section">Parts required</div>
          <p className="small muted" style={{ marginTop: 0 }}>
            A part listed here as blocking, with no stock, is what puts this machine
            on the dashboard as “down, waiting for a part”.
          </p>

          {lines.length > 0 && (
            <table className="table" style={{ marginBottom: 'var(--sp-3)' }}>
              <thead>
                <tr>
                  <th>Part</th><th style={{ textAlign: 'right' }}>Required</th>
                  <th style={{ textAlign: 'right' }}>Issued</th><th>ETA</th><th />
                </tr>
              </thead>
              <tbody>
                {lines.map((l) => {
                  const p = parts?.find((x) => String(x.id) === l.part_id);
                  return (
                    <tr key={l.id}>
                      <td>
                        {p ? `${p.part_no} — ${p.description}` : l.free_text_part}
                        {l.is_blocking && <> <StatusChip tone="red" dot={false}>Blocking</StatusChip></>}
                      </td>
                      <td style={{ textAlign: 'right' }} className="tabular">{num(l.qty_required, 0)}</td>
                      <td style={{ textAlign: 'right' }} className="tabular">{num(l.qty_issued, 0)}</td>
                      <td>{l.eta_date ?? '—'}</td>
                      <td style={{ textAlign: 'right' }}>
                        <ConfirmRemove compact onConfirm={async () =>
                          (await save(() => softDelete('breakdown_parts', l.id), 'Part removed')) !== null} />
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}

          <div className="form-grid">
            <SelectInput id="bp-part" label="Part from the catalogue" value={partId}
              options={(parts ?? []).map((p) => ({
                value: String(p.id), label: `${p.part_no} — ${p.description}`,
              }))}
              onChange={setPartId} />
            <TextInput id="bp-free" label="…or describe it" value={freeText} onChange={setFreeText}
              help="Use this when the part is not catalogued yet." />
            <NumberInput id="bp-qty" label="Quantity" min={1} step={1} value={qty} onChange={setQty} />
            <TextInput id="bp-eta" label="Expected" type="date" value={eta} onChange={setEta} />
            <div style={{ paddingTop: 22 }}>
              <Button variant="secondary" onClick={addPart} loading={busy}
                disabled={!partId && !freeText.trim()}>
                Add part
              </Button>
            </div>
          </div>
        </>
      )}

      {!isNew && (
        <p className="small muted" style={{ marginTop: 'var(--sp-4)' }}>
          Logged {dateTime(breakdown.started_at)} · {humanise(String(v.status))}
        </p>
      )}

      <Evidence
        entityType="breakdown"
        entityId={breakdown.id}
        label="Photographs"
        help="The failure, the damaged part, the repair. A photograph settles an argument about what actually broke."
      />
    </Drawer>
  );
}
